import { z } from 'zod';
import { isRealDate, paginationQuerySchema, uuidSchema } from './common.js';
import { PAYMENT_METHODS, minorSchema } from './fees.js';
const text = (max) => z
    .string()
    .trim()
    .max(max)
    .refine((v) => !v.includes('\u0000'), 'Invalid character');
const optionalText = (max) => text(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .refine(isRealDate, 'Invalid calendar date');
const atLeastOne = (v) => Object.keys(v).length > 0;
/** Removes hyphens and spaces and upper-cases a trailing X: "0-306-40615-2" -> "0306406152". */
export function normalizeIsbn(value) {
    return value.replace(/[\s-]/g, '').toUpperCase();
}
/** ISBN-10 or ISBN-13 with a correct check digit. */
export function isValidIsbn(raw) {
    const isbn = normalizeIsbn(raw);
    if (/^[0-9]{9}[0-9X]$/.test(isbn)) {
        const sum = [...isbn].reduce((s, ch, i) => s + (ch === 'X' ? 10 : Number(ch)) * (10 - i), 0);
        return sum % 11 === 0;
    }
    if (/^[0-9]{13}$/.test(isbn)) {
        const sum = [...isbn].reduce((s, ch, i) => s + Number(ch) * (i % 2 === 0 ? 1 : 3), 0);
        return sum % 10 === 0;
    }
    return false;
}
/** The canonical stored form: ISBN-10 is converted to ISBN-13 so one title cannot be filed under both. */
export function toIsbn13(raw) {
    const isbn = normalizeIsbn(raw);
    if (!/^[0-9]{9}[0-9X]$/.test(isbn))
        return isbn;
    const body = `978${isbn.slice(0, 9)}`;
    const sum = [...body].reduce((s, ch, i) => s + Number(ch) * (i % 2 === 0 ? 1 : 3), 0);
    return `${body}${(10 - (sum % 10)) % 10}`;
}
const isbnSchema = z
    .string()
    .trim()
    .transform((v) => (v === '' ? null : normalizeIsbn(v)))
    .refine((v) => v === null || isValidIsbn(v), 'Not a valid ISBN-10 or ISBN-13')
    .transform((v) => (v === null ? null : toIsbn13(v)))
    .nullable()
    .optional();
const bookFields = {
    title: text(300).min(1),
    author: text(200).min(1),
    isbn: isbnSchema,
    publisher: optionalText(200),
    category: optionalText(100),
    language: optionalText(50),
    publishedYear: z.number().int().min(1000).max(2200).nullable().optional(),
    shelf: optionalText(50),
    priceMinor: minorSchema.nullable().optional(),
};
export const createBookSchema = z.object({
    ...bookFields,
    /** How many physical copies to register now (accession numbers are generated). */
    copies: z.number().int().min(0).max(100).default(1),
});
export const updateBookSchema = z
    .object(bookFields)
    .partial()
    .refine(atLeastOne, 'At least one field is required');
export const addCopiesSchema = z.object({ count: z.number().int().min(1).max(100) });
export const updateCopySchema = z.object({ status: z.enum(['available', 'withdrawn']) });
const flag = z.enum(['true', 'false']).transform((v) => v === 'true');
export const listBooksQuerySchema = paginationQuerySchema.extend({
    q: text(100).optional(),
    category: text(100).optional(),
    availableOnly: flag.optional(),
});
// ---------------------------------------------------------------- circulation
export const BORROWER_TYPES = ['student', 'staff'];
export const issueLoanSchema = z.object({
    accessionNumber: text(40).min(1),
    borrowerType: z.enum(BORROWER_TYPES),
    borrowerId: uuidSchema,
});
export const returnLoanSchema = z.object({ damageFeeMinor: minorSchema.optional() });
export const markLostSchema = z.object({ amountMinor: minorSchema.optional() });
export const listLoansQuerySchema = paginationQuerySchema.extend({
    status: z.enum(['active', 'returned', 'lost']).optional(),
    borrowerType: z.enum(BORROWER_TYPES).optional(),
    studentId: uuidSchema.optional(),
    staffId: uuidSchema.optional(),
    overdue: flag.optional(),
});
export const listFinesQuerySchema = paginationQuerySchema.extend({
    status: z.enum(['unpaid', 'paid', 'waived']).optional(),
    studentId: uuidSchema.optional(),
    staffId: uuidSchema.optional(),
});
export const collectFineSchema = z.object({
    method: z.enum(PAYMENT_METHODS),
    reference: optionalText(100),
});
export const waiveFineSchema = z.object({ reason: text(300).min(1) });
export const popularBooksQuerySchema = z
    .object({ from: isoDate, to: isoDate })
    .refine((v) => v.to >= v.from && Date.parse(v.to) - Date.parse(v.from) <= 366 * 86_400_000, {
    message: 'Range must be ordered and at most 366 days',
    path: ['to'],
});
//# sourceMappingURL=library.js.map